import { SubProductAction } from "features/product/types/actions.subproduct";
import { ProductTable } from "features/product/types/subproductTable";
import React, { useLayoutEffect, useCallback } from "react";
import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import { BiAddToQueue } from "react-icons/bi";
import { useDispatch, useSelector } from "services/redux/rootReducer";
import { AddTableBar, Container } from "./tablesManager.styled";
import ModalManager from "../modals/modalManager";
import TableContainerModal from "../modals/tableContainerModal";
import TableBar from "./tableBar";

interface Props {
    tables: Record<string, ProductTable>;
    tableIds: string[];
    readOnlyMode?: boolean;
}

const TablesManager: React.FC<Props> = ({
    tables: initialTablesFromProps,
    tableIds: initialTableIdsFromProps,
    readOnlyMode: readOnlyModeFromProps,
}) => {
    const { tableIds, readOnlyMode, shownTableId } = useSelector(
        (state) => state.subProducts
    );
    const dispatch = useDispatch();

    const handleResetToInitialSettings = useCallback(() => {
        dispatch({
            type: SubProductAction.SET_TABLES,
            tables: initialTablesFromProps ?? {},
        });
        dispatch({
            type: SubProductAction.SET_TABLE_IDS,
            tableIds: initialTableIdsFromProps ?? {},
        });
        dispatch({
            type: SubProductAction.SET_SHOWN_TABLE_ID,
            id: "",
        });
        dispatch({
            type: SubProductAction.SET_GROUPING_MODE,
            groupingMode: false,
        });
        dispatch({
            type: SubProductAction.SET_READONLY_MODE,
            isReadOnly: readOnlyModeFromProps,
        });
    }, [
        dispatch,
        initialTableIdsFromProps,
        initialTablesFromProps,
        readOnlyModeFromProps,
    ]);

    useLayoutEffect(() => {
        handleResetToInitialSettings();
    }, [dispatch, handleResetToInitialSettings]);

    const handleAddTable = () => {
        dispatch({
            type: SubProductAction.ADD_TABLE,
        });
    };

    // in case dropped outside the list
    const handleDragEnd = (result: DropResult) => {
        // dropped outside the list
        if (!result.destination) return;

        dispatch({
            type: SubProductAction.MOVE_AND_INSERT_TABLE,
            oldIdx: result.source.index,
            newIdx: result.destination.index,
        });
    };

    const displayedTables = tableIds.map((tableId, idx) => (
        <TableBar key={tableId} id={tableId} idx={idx} />
    ));

    return (
        <>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="droppable">
                    {(provided) => (
                        <Container
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                        >
                            {!readOnlyMode && (
                                <AddTableBar onClick={handleAddTable}>
                                    <span>ADD TABLE</span>
                                    <BiAddToQueue />
                                </AddTableBar>
                            )}
                            {displayedTables}
                            {provided.placeholder}
                        </Container>
                    )}
                </Droppable>
            </DragDropContext>
            {!!shownTableId && <TableContainerModal />}
            <ModalManager />
        </>
    );
};

export default TablesManager;
